<template>
  <div class="container">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="mb-1">Erstattung</h2>
        <nav aria-label="breadcrumb">
          <ol class="breadcrumb">
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-list' }" class="text-decoration-none">
                Bereiche
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-detail', params: { id: $route.params.id } }" class="text-decoration-none">
                {{ selectedTeam?.name }}
              </router-link>
            </li>
            <li class="breadcrumb-item">
              <router-link :to="{ name: 'teams-settings', params: { id: $route.params.id } }" class="text-decoration-none">
                Bereich-Einstellungen
              </router-link>
            </li>
            <li class="breadcrumb-item active" aria-current="page">Erstattung</li>
          </ol>
        </nav>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary" @click="goBack">
          <font-awesome-icon icon="arrow-left" /> Zurück
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Content -->
    <div v-else-if="selectedTeam">
      <!-- Admin Section -->
      <div v-if="isAdminOrCreator" class="mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <font-awesome-icon icon="cog" class="me-2" />
              Erstattungseinstellungen
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveRefundSettings">
              <div class="mb-3">
                <div class="form-check form-switch">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="refundEnabled"
                    v-model="refundSettings.enabled"
                  />
                  <label class="form-check-label" for="refundEnabled">
                    <strong>Erstattungsfunktion aktivieren</strong>
                  </label>
                </div>
                <div class="form-text">
                  Aktiviere diese Option, um Erstattungen zu ermöglichen.
                </div>
              </div>

              <div v-if="refundSettings.enabled" class="mb-4">
                <h6 class="mb-3">Verfügbare Erstattungsmethoden:</h6>
                
                <div class="row">
                  <div class="col-md-4">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="paypalEnabled"
                        v-model="refundSettings.paypalEnabled"
                      />
                      <label class="form-check-label" for="paypalEnabled">
                        <font-awesome-icon icon="fab fa-paypal" class="me-2" />
                        PayPal
                      </label>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="bankTransferEnabled"
                        v-model="refundSettings.bankTransferEnabled"
                      />
                      <label class="form-check-label" for="bankTransferEnabled">
                        <font-awesome-icon icon="university" class="me-2" />
                        Banküberweisung
                      </label>
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="cashEnabled"
                        v-model="refundSettings.cashEnabled"
                      />
                      <label class="form-check-label" for="cashEnabled">
                        <font-awesome-icon icon="money-bill-wave" class="me-2" />
                        Bar
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end">
                <button type="submit" class="btn btn-primary" :disabled="savingSettings">
                  <font-awesome-icon 
                    :icon="savingSettings ? 'spinner' : 'save'" 
                    :class="{ 'fa-spin': savingSettings }" 
                    class="me-1" 
                  />
                  {{ savingSettings ? 'Speichern...' : 'Einstellungen speichern' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- SEPA Auftraggeber-Daten Section -->
      <div v-if="isAdminOrCreator && refundSettings.bankTransferEnabled" class="mb-4">
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0">
              <font-awesome-icon icon="university" class="me-2" />
              SEPA Auftraggeber-Daten
            </h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="saveSepaSettings">
              <div class="row g-3">
                <div class="col-md-6">
                  <label for="debtorName" class="form-label">Auftraggeber Name *</label>
                  <input
                    type="text"
                    id="debtorName"
                    class="form-control"
                    v-model="sepaSettings.debtorName"
                    placeholder="z.B. pronomiX GmbH"
                    required
                  />
                </div>
                <div class="col-md-6">
                  <label for="debtorIBAN" class="form-label">Auftraggeber IBAN *</label>
                  <div class="input-group">
                    <input
                      type="text"
                      id="debtorIBAN"
                      class="form-control"
                      :class="{ 'is-valid': ibanValid, 'is-invalid': ibanInvalid }"
                      v-model="sepaSettings.debtorIBAN"
                      placeholder="DE89 3704 0044 0532 0130 00"
                      required
                      @input="validateIban"
                    />
                    <span v-if="ibanValidating" class="input-group-text">
                      <div class="spinner-border spinner-border-sm" role="status">
                        <span class="visually-hidden">Validierung...</span>
                      </div>
                    </span>
                    <span v-else-if="ibanValid" class="input-group-text text-success">
                      <font-awesome-icon icon="check" />
                    </span>
                    <span v-else-if="ibanInvalid" class="input-group-text text-danger">
                      <font-awesome-icon icon="exclamation-triangle" />
                    </span>
                  </div>
                  <div v-if="ibanFeedback" class="form-text" :class="ibanValid ? 'text-success' : 'text-danger'">
                    {{ ibanFeedback }}
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="debtorBIC" class="form-label">Auftraggeber BIC *</label>
                  <input
                    type="text"
                    id="debtorBIC"
                    class="form-control"
                    v-model="sepaSettings.debtorBIC"
                    placeholder="COBADEFFXXX"
                    required
                  />
                  <div v-if="bicAutoFilled" class="form-text text-success">
                    ✓ BIC automatisch ausgefüllt
                  </div>
                </div>
                <div class="col-md-6">
                  <label for="sepaLibVersion" class="form-label">SEPA-Lib Version</label>
                  <select id="sepaLibVersion" class="form-select" v-model="sepaSettings.sepaLibVersion">
                    <option value="DE-2.6">DE 2.6 (2012)</option>
                    <option value="DE-2.7">DE 2.7 (2013)</option>
                    <option value="DE-2.8">DE 2.8 (2014)</option>
                    <option value="DE-2.9">DE 2.9 (2015)</option>
                    <option value="DE-3.0">DE 3.0 (2016)</option>
                    <option value="DE-3.1">DE 3.1 (2017)</option>
                    <option value="DE-3.5">DE 3.5 (2021)</option>
                    <option value="DE-3.7">DE 3.7 (2024)</option>
                  </select>
                </div>
              </div>
              <div class="d-flex justify-content-end mt-3">
                <button type="submit" class="btn btn-primary" :disabled="savingSepaSettings">
                  <font-awesome-icon 
                    :icon="savingSepaSettings ? 'spinner' : 'save'" 
                    :class="{ 'fa-spin': savingSepaSettings }" 
                    class="me-1" 
                  />
                  {{ savingSepaSettings ? 'Speichern...' : 'SEPA-Daten speichern' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Info Section -->
      <div class="card">
        <div class="card-header">
          <h6 class="mb-0">
            <font-awesome-icon icon="info-circle" class="me-2" />
            Informationen
          </h6>
        </div>
        <div class="card-body">
          <p class="small text-muted">
            Die Erstattungsfunktion ermöglicht es Team-Mitgliedern, Erstattungen für Quittungen zu erhalten.
          </p>
          <ul class="small text-muted">
            <li>Team-Admins können verfügbare Erstattungsmethoden konfigurieren</li>
            <li>Jedes Mitglied kann eine Erstattungsmethode wählen</li>
            <li>Bei PayPal muss der PayPal.me Name angegeben werden</li>
            <li>Bei Banküberweisung sind IBAN, BIC und Kontoinhaber erforderlich</li>
          </ul>
          
          <div v-if="refundSettings.bankTransferEnabled" class="mt-4">
            <h6 class="mb-2">SEPA-Lib Versionen:</h6>
            <div class="table-responsive">
              <table class="table table-sm table-borderless">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Datum</th>
                    <th>Überweisung</th>
                  </tr>
                </thead>
                <tbody class="small">
                  <tr><td>DE 2.6</td><td>17.11.2012</td><td>pain.001.002.03</td></tr>
                  <tr><td>DE 2.7</td><td>04.11.2013</td><td>pain.001.003.03</td></tr>
                  <tr><td>DE 2.8</td><td>17.11.2014</td><td>pain.001.003.03</td></tr>
                  <tr><td>DE 2.9</td><td>23.11.2015</td><td>pain.001.003.03</td></tr>
                  <tr><td>DE 3.0</td><td>20.11.2016</td><td>pain.001.001.03</td></tr>
                  <tr><td>DE 3.1</td><td>19.11.2017</td><td>pain.001.001.03</td></tr>
                  <tr><td>DE 3.5</td><td>21.11.2021</td><td>pain.001.001.03</td></tr>
                  <tr><td>DE 3.7</td><td>17.03.2024</td><td>pain.001.001.09</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Team not found -->
    <div v-else class="alert alert-gradient-warning" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      Team nicht gefunden.
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useTeamsStore } from '@/stores/teams'
import { useAuthStore } from '@/stores/auth'
import { showSuccess, showError } from '@/services/notification'
import { teamService } from '@/api/teams'
import { ibanApi } from '@/api/iban'
import { debounce } from 'lodash-es'

const router = useRouter()
const route = useRoute()
const teamsStore = useTeamsStore()
const authStore = useAuthStore()

const { selectedTeam, loading, error } = storeToRefs(teamsStore)
const { currentUser } = storeToRefs(authStore)

const refundSettings = ref({
  enabled: false,
  paypalEnabled: false,
  bankTransferEnabled: false,
  cashEnabled: false
})

const sepaSettings = ref({
  debtorName: '',
  debtorIBAN: '',
  debtorBIC: '',
  sepaLibVersion: 'DE-3.7'
})

const savingSettings = ref(false)
const savingSepaSettings = ref(false)
const isAdminOrCreator = ref(false)


const ibanValid = ref(false)
const ibanInvalid = ref(false)
const ibanValidating = ref(false)
const ibanFeedback = ref('')
const bicAutoFilled = ref(false)
let ibanValidationTimeout = null


watch(() => selectedTeam.value, async (newTeam) => {
  if (newTeam && newTeam.id) {

    try {
      const settings = await teamService.getTeamRefundSettings(newTeam.id)
      refundSettings.value = {
        enabled: settings.enabled || false,
        paypalEnabled: settings.paypalEnabled || false,
        bankTransferEnabled: settings.bankTransferEnabled || false,
        cashEnabled: settings.cashEnabled || false
      }
    } catch (error) {
      console.warn('Erstattungseinstellungen konnten nicht geladen werden:', error)
    }


    try {
      const sepaData = await teamService.getTeamSepaSettings(newTeam.id)
      sepaSettings.value = {
        debtorName: sepaData.debtorName || '',
        debtorIBAN: sepaData.debtorIBAN || '',
        debtorBIC: sepaData.debtorBIC || '',
        sepaLibVersion: sepaData.sepaLibVersion || 'DE-3.7'
      }
    } catch (error) {
      console.warn('SEPA-Einstellungen konnten nicht geladen werden:', error)
    }
  }
}, { immediate: false })

const goBack = () => {
  router.push({ name: 'teams-settings', params: { id: route.params.id } })
}


const validateIban = debounce(async (event) => {
  const iban = event.target.value
  if (!iban || iban.length < 10) {
    ibanValid.value = false
    ibanInvalid.value = false
    ibanFeedback.value = ''
    bicAutoFilled.value = false
    return
  }

  ibanValidating.value = true
  ibanValid.value = false
  ibanInvalid.value = false
  ibanFeedback.value = ''

  try {
    const result = await ibanApi.validateIban(iban)
    
    if (result.valid) {
      ibanValid.value = true
      ibanInvalid.value = false
      
      if (result.bankName) {
        ibanFeedback.value = `${result.bankName}`
      }
      

      if (result.bic && !sepaSettings.value.debtorBIC) {
        sepaSettings.value.debtorBIC = result.bic
        bicAutoFilled.value = true
      }
    } else {
      ibanValid.value = false
      ibanInvalid.value = true
      ibanFeedback.value = result.error || 'Ungültige IBAN'
    }
  } catch (error) {
    console.error('IBAN-Validierungsfehler:', error)
    ibanValid.value = false
    ibanInvalid.value = true
    ibanFeedback.value = 'Fehler bei der IBAN-Validierung'
  } finally {
    ibanValidating.value = false
  }
}, 500)

const saveSepaSettings = async () => {
  if (!selectedTeam.value) return

  savingSepaSettings.value = true
  try {
    await teamService.saveTeamSepaSettings(selectedTeam.value.id, sepaSettings.value)
    

    await teamsStore.loadTeamById(selectedTeam.value.id)
    
    showSuccess('SEPA-Daten erfolgreich gespeichert!')
  } catch (error) {
    console.error('Fehler beim Speichern der SEPA-Daten:', error)

    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Speichern der SEPA-Daten. Bitte versuch es erneut.'
    showError(errorMessage)
  } finally {
    savingSepaSettings.value = false
  }
}

const saveRefundSettings = async () => {
  if (!selectedTeam.value) return

  savingSettings.value = true
  try {
    const result = await teamService.updateTeamRefundSettings(selectedTeam.value.id, refundSettings.value)
    

    await teamsStore.loadTeamById(selectedTeam.value.id)
    

    if (result.resetUsers && result.resetUsers.length > 0) {
      const methodNames = {
        'paypal': 'PayPal',
        'bank_transfer': 'Banküberweisung',
        'cash': 'Bar'
      };
      
      const resetMethods = [...new Set(result.resetUsers.map(u => methodNames[u.refundMethod] || u.refundMethod))];
      const methodText = resetMethods.length === 1 ? resetMethods[0] : resetMethods.join(', ');
      
      showSuccess(`Erstattungseinstellungen erfolgreich gespeichert! ${result.resetUsers.length} Benutzer mit der Methode "${methodText}" wurden zurückgesetzt.`);
    } else {
      showSuccess('Erstattungseinstellungen erfolgreich gespeichert!')
    }
  } catch (error) {
    console.error('Fehler beim Speichern der Erstattungseinstellungen:', error)

    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Fehler beim Speichern der Einstellungen. Bitte versuch es erneut.'
    showError(errorMessage)
  } finally {
    savingSettings.value = false
  }
}

onMounted(async () => {

  if (!selectedTeam.value || selectedTeam.value.id !== route.params.id) {
    await teamsStore.loadTeamById(route.params.id)
  }
  

  if (selectedTeam.value && selectedTeam.value.id) {

    isAdminOrCreator.value = (
      selectedTeam.value.role === 'teamAdmin' || 
      selectedTeam.value.creatorId === currentUser.value?.id
    )
    

    try {
      const settings = await teamService.getTeamRefundSettings(selectedTeam.value.id)
      refundSettings.value = {
        enabled: settings.enabled || false,
        paypalEnabled: settings.paypalEnabled || false,
        bankTransferEnabled: settings.bankTransferEnabled || false,
        cashEnabled: settings.cashEnabled || false
      }
    } catch (error) {
      console.warn('Erstattungseinstellungen konnten nicht geladen werden:', error)
    }


    try {
      const sepaData = await teamService.getTeamSepaSettings(selectedTeam.value.id)
      sepaSettings.value = {
        debtorName: sepaData.debtorName || '',
        debtorIBAN: sepaData.debtorIBAN || '',
        debtorBIC: sepaData.debtorBIC || '',
        sepaLibVersion: sepaData.sepaLibVersion || 'DE-3.7'
      }
    } catch (error) {
      console.warn('SEPA-Einstellungen konnten nicht geladen werden:', error)
    }
  }
})
</script>
